import { useState } from 'react'
import { motion } from 'framer-motion'
import { XMarkIcon, ClockIcon } from '@heroicons/react/24/outline'
import { useAuth0 } from '@auth0/auth0-react'
import { formatDateLocal } from '../utils/dateUtils'
import { useUsersApi } from '../services/usersApi'

interface FoodItem {
  id: string
  category: string
  description: string
  assignedTo?: string
  assignedUserName?: string
  servedDate?: string
  servings?: number
  notes?: string
}

interface CreateFoodItemModalProps {
  isOpen: boolean
  date: string
  onClose: () => void
  onCreateFoodItem: (date: string, foodData: Omit<FoodItem, 'id'>) => void
}

const FOOD_CATEGORIES = [
  'Main Dish',
  'Side Dish',
  'Appetizer',
  'Dessert',
  'Beverages',
  'Snacks',
  'Breakfast',
  'Lunch',
  'Dinner',
  'Other'
]

export default function CreateFoodItemModal({ 
  isOpen, 
  date,
  onClose, 
  onCreateFoodItem 
}: CreateFoodItemModalProps) {
  const { user } = useAuth0()
  const usersApi = useUsersApi()
  const [formData, setFormData] = useState({
    category: 'Main Dish',
    description: '',
    servedDate: '',
    servings: 1,
    notes: '',
    assignToMe: false
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.description) {
      return
    }

    // Get proper user name if assigning to self
    let assignedUserName: string | undefined = undefined
    if (formData.assignToMe && user?.sub) {
      try {
        const userData = await usersApi.getUser(user.sub)
        assignedUserName = userData.name || `${userData.firstName || ''} ${userData.lastName || ''}`.trim()
      } catch (error) {
        // Fallback to Auth0 user data if API fails
        assignedUserName = user?.name || `${user?.given_name || ''} ${user?.family_name || ''}`.trim()
      }
    }

    const payload: Omit<FoodItem, 'id'> = {
      category: formData.category,
      description: formData.description,
      servedDate: formData.servedDate || undefined,
      servings: Math.max(1, Number(formData.servings) || 1),
      notes: formData.notes || undefined,
      assignedTo: formData.assignToMe ? user?.sub : undefined,
      assignedUserName,
    }

    onCreateFoodItem(date, payload)

    // Reset form
    setFormData({
      category: 'Main Dish',
      description: '',
      servedDate: '',
      servings: 1,
      notes: '',
      assignToMe: false
    })

    onClose()
  }

  // Set default served date to the selected date at dinner time
  const getDefaultServedTime = () => {
    if (!formData.servedDate) {
      // Parse the date string manually to avoid timezone conversion
      const [year, month, day] = date.split('-').map(Number)
      const defaultTime = new Date(year, month - 1, day, 18, 0) // 6:00 PM default
      return defaultTime.toISOString().slice(0, 16) // Format for datetime-local
    }
    return formData.servedDate
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full"
      >
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Add Food Item
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Date Display */}
          <div className="bg-primary-50 dark:bg-primary-900/20 rounded-lg p-4">
            <h4 className="text-sm font-medium text-primary-900 dark:text-primary-100">
              Food for: {formatDateLocal(date, { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long',
                day: 'numeric' 
              })}
            </h4>
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Category
            </label>
            <select
              value={formData.category}
              onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              {FOOD_CATEGORIES.map(category => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Food Description *
            </label>
            <input
              type="text"
              required
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="e.g., BBQ Ribs (serves 8-10), Caesar Salad, Apple Pie"
            />
          </div>

          {/* Served Time */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <ClockIcon className="w-4 h-4 inline mr-1" />
              When will this be served?
            </label>
            <input
              type="datetime-local"
              value={formData.servedDate || getDefaultServedTime()}
              onChange={(e) => setFormData(prev => ({ ...prev, servedDate: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              When do you plan to serve this food? This helps with meal planning.
            </p>
          </div>

          {/* Servings */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Servings
            </label>
            <input
              type="number"
              min={1}
              value={formData.servings}
              onChange={(e) => setFormData(prev => ({ ...prev, servings: Number(e.target.value) }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="e.g., 8"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Roughly how many people will this serve?
            </p>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              rows={3}
              placeholder="Optional: dietary notes, ingredients, prep needs, etc."
            />
          </div>

          {/* Assign to me */}
          <div className="flex items-center">
            <input
              id="assignToMe"
              type="checkbox"
              checked={formData.assignToMe}
              onChange={(e) => setFormData(prev => ({ ...prev, assignToMe: e.target.checked }))}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="assignToMe" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Assign to me
            </label>
          </div>

          {/* Examples */}
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Examples:</h4>
            <ul className="text-xs text-gray-600 dark:text-gray-300 space-y-1">
              <li>• <strong>Main Dish:</strong> Grilled Chicken (serves 12), Vegetarian Chili</li>
              <li>• <strong>Side Dish:</strong> Mac & Cheese, Green Bean Casserole</li>
              <li>• <strong>Appetizer:</strong> Deviled Eggs, Spinach Artichoke Dip</li>
              <li>• <strong>Dessert:</strong> Chocolate Cake, Fresh Fruit Tray</li>
              <li>• <strong>Beverages:</strong> Coffee & Tea, Soft Drinks, Beer</li>
            </ul>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!formData.description}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Add Food Item
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  )
}
